/**
 * Isolates evolving publisher metadata from live-state UI code so unknown or
 * malformed backend shapes degrade to missing values instead of crashing views.
 */

export interface GoogleAssetMetadata {
  gti?: string;
  assetType?: string;
  assetUrl?: string;
  name?: string;
  placement?: string;
  description?: string;
}

export interface GoogleAdMetadata {
  gti?: string;
  name?: string;
  refTag?: string;
  adType?: string;
  destinationUrl?: string;
  headlines?: string[];
  descriptions?: string[];
  longHeadlines?: string[];
  businessName?: string;
  callToActionText?: string;
}

export interface GoogleCarouselCardMetadata {
  gti?: string;
  /** Zero-based position of the card in its carousel. */
  cardIndex?: number;
  headline?: string;
  callToActionText?: string;
  destinationUrl?: string;
}

interface GoogleChannelControls {
  selected_channels?: Record<string, boolean>;
}

export interface GoogleAdGroupMetadata {
  campaignId?: string;
  name?: string;
  status?: string;
  channelControls?: GoogleChannelControls;
  useAudienceGrouped?: boolean;
}

interface GoogleBiddingStrategy {
  type?: string;
  targetCpaMicros?: string;
}

export interface GoogleCampaignMetadata {
  budgetResourceName?: string;
  amountMicros?: string;
  totalAmountMicros?: string;
  endDateTime?: string;
  name?: string;
  biddingStrategy?: GoogleBiddingStrategy;
}

type MetadataRecord = Record<string, unknown>;

const ASSET_METADATA_KEYS = [
  'gti',
  'assetType',
  'assetUrl',
  'name',
  'placement',
  'description',
] as const;
const AD_METADATA_KEYS = [
  'gti',
  'name',
  'refTag',
  'adType',
  'destinationUrl',
  'headlines',
  'descriptions',
  'longHeadlines',
  'businessName',
  'callToActionText',
] as const;
const CAMPAIGN_METADATA_KEYS = [
  'budgetResourceName',
  'amountMicros',
  'totalAmountMicros',
  'endDateTime',
  'biddingStrategy',
] as const;

function isMetadataRecord (value: unknown): value is MetadataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasAnyKey (metadata: MetadataRecord, keys: readonly string[]): boolean {
  return keys.some(key => key in metadata);
}

function copyStringField<T extends MetadataRecord> (
  source: MetadataRecord,
  target: T,
  key: keyof T,
): void {
  const value = source[key as string];
  if (typeof value === 'string') target[key] = value as T[keyof T];
}

function narrowTextArray (value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const values = value.map(item => {
    if (typeof item === 'string') return item;
    if (isMetadataRecord(item) && typeof item.text === 'string') return item.text;
    return undefined;
  });
  return values.every((item): item is string => item !== undefined) ? values : undefined;
}

function narrowBiddingStrategy (value: unknown): GoogleBiddingStrategy | undefined {
  if (!isMetadataRecord(value)) return undefined;

  const strategy: GoogleBiddingStrategy = {};
  copyStringField(value, strategy as MetadataRecord, 'type');
  copyStringField(value, strategy as MetadataRecord, 'targetCpaMicros');
  return Object.keys(strategy).length > 0 ? strategy : undefined;
}

function narrowChannelControls (value: unknown): GoogleChannelControls | undefined {
  if (!isMetadataRecord(value) || !isMetadataRecord(value.selected_channels)) return undefined;
  const selectedChannels = Object.entries(value.selected_channels);
  if (!selectedChannels.every(([, selected]) => typeof selected === 'boolean')) return undefined;
  return { selected_channels: Object.fromEntries(selectedChannels) as Record<string, boolean> };
}

export function narrowAssetMetadata (meta: unknown): GoogleAssetMetadata | undefined {
  try {
    if (!isMetadataRecord(meta) || !hasAnyKey(meta, [...ASSET_METADATA_KEYS, 'storageLink'])) return undefined;

    const metadata: GoogleAssetMetadata = {};
    ASSET_METADATA_KEYS.forEach(key => {
      copyStringField(meta, metadata as MetadataRecord, key);
    });
    // ADSP stores the rendered file URL as `storageLink` (the ACP asset field) rather than Google's
    // `assetUrl`; fall back to it so ADSP assets get a media preview.
    if (metadata.assetUrl === undefined && typeof meta.storageLink === 'string') {
      metadata.assetUrl = meta.storageLink;
    }
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  } catch {
    return undefined;
  }
}

export function narrowAdMetadata (meta: unknown): GoogleAdMetadata | undefined {
  try {
    if (!isMetadataRecord(meta) || !hasAnyKey(meta, AD_METADATA_KEYS)) return undefined;

    const metadata: GoogleAdMetadata = {};
    const stringFields = [
      'gti', 'name', 'refTag', 'adType', 'destinationUrl', 'businessName', 'callToActionText',
    ] as const;
    stringFields.forEach(key => { copyStringField(meta, metadata as MetadataRecord, key); });

    const arrayFields = ['headlines', 'descriptions', 'longHeadlines'] as const;
    arrayFields.forEach(key => {
      const values = narrowTextArray(meta[key]);
      if (values) metadata[key] = values;
    });
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  } catch {
    return undefined;
  }
}

export function narrowCarouselCardMetadata (meta: unknown): GoogleCarouselCardMetadata | undefined {
  try {
    if (!isMetadataRecord(meta)) return undefined;

    const metadata: GoogleCarouselCardMetadata = {};
    const stringFields = ['gti', 'headline', 'callToActionText', 'destinationUrl'] as const;
    stringFields.forEach(key => { copyStringField(meta, metadata as MetadataRecord, key); });
    if (typeof meta.cardIndex === 'number' && Number.isInteger(meta.cardIndex) && meta.cardIndex >= 0) {
      metadata.cardIndex = meta.cardIndex;
    }
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  } catch {
    return undefined;
  }
}

export function narrowAdGroupMetadata (meta: unknown): GoogleAdGroupMetadata | undefined {
  try {
    if (!isMetadataRecord(meta)) return undefined;
    if (!hasAnyKey(meta, ['campaignId', 'name', 'status', 'channelControls', 'useAudienceGrouped'])) return undefined;

    const metadata: GoogleAdGroupMetadata = {};
    const stringFields = ['campaignId', 'name', 'status'] as const;
    stringFields.forEach(key => { copyStringField(meta, metadata as MetadataRecord, key); });
    const channelControls = narrowChannelControls(meta.channelControls);
    if (channelControls) metadata.channelControls = channelControls;
    if (typeof meta.useAudienceGrouped === 'boolean') {
      metadata.useAudienceGrouped = meta.useAudienceGrouped;
    }
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  } catch {
    return undefined;
  }
}

export function narrowCampaignMetadata (meta: unknown): GoogleCampaignMetadata | undefined {
  try {
    if (!isMetadataRecord(meta) || !hasAnyKey(meta, CAMPAIGN_METADATA_KEYS)) return undefined;

    const metadata: GoogleCampaignMetadata = {};
    const stringFields = [
      'budgetResourceName', 'amountMicros', 'totalAmountMicros', 'endDateTime', 'name',
    ] as const;
    stringFields.forEach(key => { copyStringField(meta, metadata as MetadataRecord, key); });
    const biddingStrategy = narrowBiddingStrategy(meta.biddingStrategy);
    if (biddingStrategy) metadata.biddingStrategy = biddingStrategy;
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  } catch {
    return undefined;
  }
}
